import { mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { Globe, Split, Undo2 } from 'lucide-react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
  UnitBase,
  UnitInheritance
} from 'v5/core/form-schema/formSchema.types';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { renderSpinnerMutation } from 'v5/patterns/widgets/renderSpinnerMutation';
import type { ClearAction } from './schemaForm.types';
import { UnitEditor, type UnitEditorProps } from './UnitEditor';
import { UnitErrorAlert } from './UnitErrorAlert';
import { useReadOnlyReasonLabel } from './useReadOnlyReasonLabel';

export type SchemaUnitEditorProps<F extends FieldValues> = Omit<
  UnitEditorProps<F>,
  'unit' | 'titleExtras' | 'restActions' | 'restNotice' | 'compactTitleRow'
> & {
  unit: UnitBase;
  /** Drops the unit's own value for the one it inherits from Global. */
  clear?: ClearAction;
  /** Global custom-field units: names of workspaces holding an override. */
  overriddenIn?: string[];
};

/**
 * `UnitEditor` for a unit of a schema: what the unit's workspace scope adds
 * around the editor (where its value comes from, "Inherit from global",
 * where it is overridden, why it is read-only).
 */
export function SchemaUnitEditor<F extends FieldValues>({
  unit,
  clear = { type: 'not-applicable' },
  overriddenIn = [],
  ...rest
}: SchemaUnitEditorProps<F>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SchemaForm.unit' });
  const readOnlyReasonLabel = useReadOnlyReasonLabel();
  const clearMutation = promiseState.useMutation(async () => {
    if (clear.type === 'available') await clear.run();
  });
  const { editability } = unit;

  return (
    <UnitEditor<F>
      {...rest}
      compactTitleRow
      unit={{
        id: unit.id,
        title: unit.title,
        editability:
          editability.type === 'editable'
            ? { type: 'editable' }
            : {
                type: 'read-only',
                reason: readOnlyReasonLabel(editability.reason, 'field')
              }
      }}
      titleExtras={({ isEditing }) => (
        <>
          <InheritanceBadge
            inheritance={unit.inheritance}
            compact={isEditing}
          />
          {overriddenIn.length > 0 && (
            // Just the count: beside the edit actions a longer badge would wrap
            // under the title. The tooltip names the workspaces.
            <SimpleTooltip
              title={t('overriddenIn', { workspaces: overriddenIn.join(', ') })}
            >
              <Badge
                variant="attention"
                tone="subtle"
                size="sm"
                className="tabular-nums"
              >
                <span aria-hidden="true">{overriddenIn.length}</span>
                <span className="sr-only">
                  {t('overriddenIn', { workspaces: overriddenIn.join(', ') })}
                </span>
              </Badge>
            </SimpleTooltip>
          )}
        </>
      )}
      restActions={
        clear.type === 'available' &&
        unit.inheritance.type === 'overridden' && (
          <SimpleTooltip title={t('inheritFromGlobal')} align="end">
            <Button
              variant="ghost"
              size="sm"
              layout="icon"
              // A failure shows as `restNotice`; it needs no second report.
              onClick={() =>
                void clearMutation.track(undefined).catch(() => {})
              }
              visuallyDisabled={mt.isInProgress(clearMutation.state)}
              aria-label={t('inheritLabel', { title: unit.title })}
            >
              {renderSpinnerMutation(clearMutation.state, <Undo2 />)}
            </Button>
          </SimpleTooltip>
        )
      }
      restNotice={
        mt.isInError(clearMutation.state) && (
          <UnitErrorAlert
            title={t('inheritFailed')}
            message={clearMutation.state.error.message}
            onClose={clearMutation.reset}
          />
        )
      }
    />
  );
}

/**
 * Where a workspace unit's value comes from, beside its title. "Inherited",
 * the usual state, is just its icon; "Overridden", the exception, also says
 * so, except while editing, beside the wider Cancel and Save, so it never
 * wraps under the title. The tooltip and the icon's accessible name keep the
 * words.
 */
function InheritanceBadge({
  inheritance,
  compact
}: {
  inheritance: UnitInheritance;
  compact: boolean;
}) {
  const { t } = useTranslation('patterns');
  if (inheritance.type === 'not-applicable') return null;
  const inherited = inheritance.type === 'inherited';
  const label = t(
    inherited ? 'SchemaForm.unit.inherited' : 'SchemaForm.unit.overridden'
  );
  const iconOnly = inherited || compact;
  const meaning = t(
    inherited ? 'SchemaForm.badge.inherited' : 'SchemaForm.badge.overridden'
  );
  const badge = (
    <Badge
      variant={inherited ? 'gray' : 'accent'}
      tone="subtle"
      size="sm"
      layout={iconOnly ? 'icon' : 'normal'}
      className="whitespace-nowrap"
      {...(iconOnly ? { role: 'img', 'aria-label': label } : {})}
    >
      {inherited ? <Globe aria-hidden /> : <Split aria-hidden />}
      {!iconOnly && <span>{label}</span>}
    </Badge>
  );
  return (
    <SimpleTooltip title={iconOnly ? `${label}. ${meaning}` : meaning}>
      {badge}
    </SimpleTooltip>
  );
}
